<div class="justup-dialog">
  <vl-dialog-header-export
    [exporting]="exporting()"
    (close)="dialogRef.close()"
    (export)="exportExcel()">
    CA N vs N-1 — {{ scaleLabelSingularLower[scale()] }}
  </vl-dialog-header-export>

  <div class="justup-dialog-content flex-col gap-16">

    <div class="flex-row justify-center">
      <vl-chips-toggle [elements]="scaleElements" [(value)]="scale" size="small"/>
    </div>

    <!-- Stats cards -->
    <div class="flex-row gap-8 wrap">
      <vl-radio-card mode="display" color="valid" icon="euro"
                     title="CA (N)"
                     style="flex: 1; min-width: 140px">
        <span content class="headline">{{ stats().totalNFormatted }}</span>
      </vl-radio-card>

      <vl-radio-card mode="display" color="secondary" icon="history"
                     title="CA (N-1)"
                     style="flex: 1; min-width: 140px">
        <span content class="headline">{{ stats().totalN1Formatted }}</span>
      </vl-radio-card>

      <vl-radio-card mode="display"
                     [color]="stats().globalDeltaPct !== null && stats().globalDeltaPct >= 0 ? 'valid' : 'error'"
                     icon="trending_up"
                     title="Évolution globale"
                     style="flex: 1; min-width: 140px">
        <div content class="flex-col gap-2">
          <span class="headline">{{ stats().globalDeltaLabel }}</span>
          <span class="tiny" style="opacity: 0.7">{{ formatEuroExact(stats().globalDeltaEur) }}</span>
        </div>
      </vl-radio-card>

      <vl-radio-card mode="display" color="secondary" icon="emoji_events"
                     [title]="scaleBestLabel[scale()]"
                     style="flex: 1; min-width: 140px">
        <div content class="flex-col gap-2">
          <span class="body-1" style="font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap">
            {{ stats().bestGrowthLabel }}
          </span>
          <span class="tiny" style="opacity: 0.7">{{ stats().bestGrowthDelta }}</span>
        </div>
      </vl-radio-card>
    </div>

    <!-- Column headers -->
    <div class="flex-row gap-8 p-8 align-center"
         style="padding-right: 12px; border-bottom: 1px solid var(--color-on-disabled)">
      <span class="shrink-0" style="width: 16px"></span>
      <span class="grow tiny on-background-alt"
            style="font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em">
        {{ scaleLabel[scale()] | titlecase }}
      </span>
      <span class="tiny on-background-alt shrink-0"
            matTooltip="CA année en cours"
            style="min-width: 90px; text-align: right; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em">
        CA (N)
      </span>
      <span class="tiny on-background-alt shrink-0"
            matTooltip="CA année précédente"
            style="min-width: 90px; text-align: right; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em">
        CA (N-1)
      </span>
      <span class="tiny on-background-alt shrink-0"
            matTooltip="Variation N vs N-1"
            style="min-width: 70px; text-align: right; font-weight: 600; text-transform: uppercase; letter-spacing: 0.06em">
        Δ
      </span>
    </div>

    <!-- Period rows -->
    @for (row of rows(); track row.relKey) {
      <div>
        <div class="flex-row gap-8 p-8 align-center border-box border-8 cursor-pointer"
             style="border: 1px solid var(--color-outline)"
             (click)="togglePeriod(row.relKey)">
          <mat-icon class="on-disabled shrink-0" style="font-size: 16px; width: 16px; height: 16px">
            {{ isExpanded(row.relKey) ? 'expand_more' : 'chevron_right' }}
          </mat-icon>
          <div class="grow flex-col" style="min-width: 0">
            <span class="caption on-background-alt" style="overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-weight: 600">
              {{ row.label }}
            </span>
            <span class="tiny on-disabled">{{ row.nbClients }} client{{ row.nbClients > 1 ? 's' : '' }}</span>
          </div>
          <span class="tiny on-background-alt shrink-0" style="min-width: 90px; text-align: right">
            {{ formatEuroExact(row.caN) }}
          </span>
          <span class="tiny on-disabled shrink-0" style="min-width: 90px; text-align: right">
            {{ formatEuroExact(row.caN1) }}
          </span>
          <span class="shrink-0 delta-badge" [style.background-color]="colorForDelta(row.deltaPct)"
                style="min-width: 70px; text-align: right">
            {{ formatDeltaPct(row.deltaPct) }}
          </span>
        </div>

        @if (isExpanded(row.relKey) && row.clients.length > 0) {
          <div class="flex-col gap-2 mt-2 mb-2" style="margin-left: 24px">
            @for (c of row.clients; track c.denomination) {
              <div class="flex-row gap-12 p-8 border-on-disabled border-box border-8">
                <div class="flex-col gap-4 grow" style="min-width: 0">
                  <span class="caption on-background-alt" style="font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap">
                    {{ c.denomination }}
                  </span>
                  <div class="flex-row gap-4 align-baseline">
                    <span class="tiny on-disabled" style="min-width: 30px">N</span>
                    <span class="tiny on-background-alt">{{ formatEuroExact(c.caN) }}</span>
                    <span class="tiny on-disabled" style="margin-left: 8px; min-width: 30px">N-1</span>
                    <span class="tiny on-background-alt">{{ formatEuroExact(c.caN1) }}</span>
                  </div>
                </div>
                <div class="flex-col gap-2 shrink-0 align-end justify-center">
                  <span class="delta-badge" [style.background-color]="colorForDelta(c.deltaPct)">
                    {{ formatDeltaPct(c.deltaPct) }}
                  </span>
                  <span class="tiny on-disabled">{{ formatEuroExact(c.deltaEur) }}</span>
                </div>
              </div>
            }
          </div>
        }
      </div>
    } @empty {
      <div class="flex-row justify-center p-32">
        <span class="caption on-background-alt">Aucune donnée sur la période</span>
      </div>
    }
  </div>
</div>
